/**
 * useSubjectsData - Manages all subjects data (for all-subjects view)
 *
 * This hook manages the list of all subjects for a grade, including their
 * terms, weeks, and lessons. Used by SubjectContainer, AllSubjectsContainer,
 * and AchievementsContainer.
 *
 * Follows the URL-as-source-of-truth pattern established by useTermsData:
 * - Auto-fetches when urlGradeCode changes
 * - Derives selectedSubject from urlSubjectSlug via useMemo
 * - Supports manual override (cleared on URL change) for optimistic in-app nav
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { subjectsApi } from "@/api/subjectsApi";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import type { RootSubject } from "@/ts/interfaces/subjects/api/subjectsApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";

interface UseSubjectsDataParams {
  urlGradeCode: number | undefined;
  urlSubjectSlug: string | undefined;
}

interface SubjectsDataState {
  // Data
  subjects: RootSubject[];
  selectedSubject: RootSubject | null;
  forGradeCode: number | undefined;

  // Status
  subjectsStatus: REQUEST_STATUS;
  subjectsError: ErrorInterface | null;
}

interface SubjectsDataActions {
  setSelectedSubject: (subject: RootSubject | null) => void;
  invalidateSubjectsCache: () => void;
}

export type SubjectsData = SubjectsDataState & SubjectsDataActions;

export function useSubjectsData({
  urlGradeCode,
  urlSubjectSlug
}: UseSubjectsDataParams): SubjectsData {
  const [subjects, setSubjects] = useState<RootSubject[]>([]);
  const [manualSubject, setManualSubject] = useState<RootSubject | null>(null);
  const [subjectsStatus, setSubjectsStatus] = useState<REQUEST_STATUS>(
    REQUEST_STATUS.IDLE
  );
  const [subjectsError, setSubjectsError] = useState<ErrorInterface | null>(
    null
  );

  const lastFetchedGradeRef = useRef<number | undefined>(undefined);
  // Counter to force refetch when incremented (mirrors useWeekLessonsData)
  const [refetchCounter, setRefetchCounter] = useState(0);

  // Invalidate cache so next render cycle will refetch.
  //
  // Both lines below are necessary:
  //  • The ref reset defeats the same-grade early-return guard (line ~81),
  //    which would otherwise see `lastFetchedGradeRef === urlGradeCode` and
  //    skip the fetch even though the effect re-ran.
  //  • The counter bump is what actually re-triggers the effect, since ref
  //    mutations alone don't cause React to re-run effects.
  //
  // See: PLAN-313.
  const invalidateSubjectsCache = useCallback(() => {
    lastFetchedGradeRef.current = undefined;
    setRefetchCounter(c => c + 1);
  }, []);

  // Auto-fetch when the target grade changes or the cache is invalidated.
  useEffect(() => {
    if (!urlGradeCode) {
      setSubjects([]);
      setSubjectsStatus(REQUEST_STATUS.IDLE);
      setSubjectsError(null);
      lastFetchedGradeRef.current = undefined;
      return;
    }

    // Already fetched for this grade during the current cache window.
    if (lastFetchedGradeRef.current === urlGradeCode) {
      return;
    }

    lastFetchedGradeRef.current = urlGradeCode;
    setSubjectsStatus(REQUEST_STATUS.PENDING);
    setSubjectsError(null);

    subjectsApi
      .getLessonsBySubject(urlGradeCode)
      .then(response => {
        // Guard against stale responses when grade changed mid-flight
        if (lastFetchedGradeRef.current !== urlGradeCode) return;
        // A malformed/non-array body (e.g. an error envelope from a 5xx)
        // must not reach state, or selectedSubject's .find() would throw.
        setSubjects(Array.isArray(response) ? response : []);
        setSubjectsStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        // Guard against stale responses when grade changed mid-flight
        if (lastFetchedGradeRef.current !== urlGradeCode) return;
        console.error("Error fetching subjects:", error);
        setSubjectsStatus(REQUEST_STATUS.ERROR);
        lastFetchedGradeRef.current = undefined;

        const err = error as {
          customAttributes?: { httpErrorStatus?: number };
          message?: string;
        };
        setSubjectsError({
          code: err?.customAttributes?.httpErrorStatus ?? 500,
          message: err?.message ?? "Failed to fetch subjects"
        });
      });
  }, [urlGradeCode, refetchCounter]);

  // Clear manual selection when URL changes
  useEffect(() => {
    setManualSubject(null);
  }, [urlSubjectSlug]);

  // Derive selectedSubject from URL or manual override
  const selectedSubject = useMemo(() => {
    if (manualSubject) return manualSubject;
    if (!urlSubjectSlug || subjectsStatus !== REQUEST_STATUS.SUCCESS)
      return null;
    if (!Array.isArray(subjects)) return null;
    return subjects.find(s => s.slug === urlSubjectSlug) ?? null;
  }, [manualSubject, urlSubjectSlug, subjects, subjectsStatus]);

  return {
    // State
    subjects,
    selectedSubject,
    forGradeCode: lastFetchedGradeRef.current,
    subjectsStatus,
    subjectsError,

    // Actions
    setSelectedSubject: setManualSubject,
    invalidateSubjectsCache
  };
}
